import React, { type JSX, type ReactNode } from 'react';
import type { ButtonProps } from '../Button/Button.tsx';
import {
  StyledDrawerActionButton,
  StyledDrawerContainerDiv,
  StyledDrawerContentContainerDiv,
  StyledDrawerWrapperDiv,
} from './DrawerActionBar.styled.ts';
import { DrawerDivider } from './DrawerDivider.tsx';

export interface DrawerAction extends Omit<ButtonProps, 'children'> {
  label: string;
  customRender?: () => ReactNode;
  'data-test'?: string;
  'data-pendo-feature'?: string;
}

export type DrawerActions = DrawerAction[];

export interface DrawerActionBarProps {
  actions: DrawerActions;
  gutterTop?: boolean;
  gutterBottom?: boolean;
  variant?: ButtonProps['variant'];
  stickyBottom?: boolean;
  hideDivider?: boolean;
  backgroundColor?: string;
  sidePadding?: boolean;
  dataTest?: string;
}

export function DrawerActionBar(props: DrawerActionBarProps): JSX.Element {
  const {
    actions,
    variant = 'contained',
    stickyBottom = false,
    hideDivider = false,
    dataTest,
    gutterBottom,
    gutterTop,
    sidePadding,
    backgroundColor,
  } = props;

  return (
    <StyledDrawerWrapperDiv
      data-test={dataTest}
      stickyBottom={stickyBottom}
      gutterBottom={gutterBottom}
      gutterTop={gutterTop}
    >
      <StyledDrawerContainerDiv stickyBottom={stickyBottom} bgColor={backgroundColor} sidePadding={sidePadding}>
        {stickyBottom && !hideDivider && <DrawerDivider gutterBottom />}

        <StyledDrawerContentContainerDiv variant={variant} data-test="action-bar">
          {actions.map(action => {
            const { label, customRender, ...restActionProps } = action;

            if (customRender) return <React.Fragment key={label}>{customRender()}</React.Fragment>;

            return (
              <StyledDrawerActionButton variant={variant} {...restActionProps} key={label}>
                {label}
              </StyledDrawerActionButton>
            );
          })}
        </StyledDrawerContentContainerDiv>
      </StyledDrawerContainerDiv>
    </StyledDrawerWrapperDiv>
  );
}
